// Common utility functions for HSP application

/**
 * Format date to readable string
 * @param {Date} date - Date object to format
 * @returns {string} Formatted date string
 */
export const formatDate = (date) => {
  if (!date) return 'Not specified';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Format currency amount
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default: USD)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency
  }).format(amount);
};

/**
 * Validate email address
 * @param {string} email - Email to validate
 * @returns {boolean} True if valid email
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate phone number (basic US format)
 * @param {string} phone - Phone number to validate
 * @returns {boolean} True if valid phone
 */
export const isValidPhone = (phone) => {
  const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
  return phoneRegex.test(phone.replace(/\D/g, ''));
};

/**
 * Truncate text to specified length
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated text
 */
export const truncateText = (text, maxLength = 100) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * Generate unique ID
 * @returns {string} Unique identifier
 */
export const generateId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

/**
 * Debounce function to limit function calls
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Get service frequency display name
 * @param {string} frequency - Frequency value
 * @returns {string} Display name
 */
export const getFrequencyDisplayName = (frequency) => {
  const frequencyMap = {
    'monthly': 'Monthly',
    '3months': 'Every 3 Months',
    '6months': 'Every 6 Months',
    'annual': 'Annual'
  };
  return frequencyMap[frequency] || frequency;
};

/**
 * Calculate total value from quotes array
 * @param {Array} quotes - Array of quote objects
 * @returns {number} Total value
 */
export const calculateTotalValue = (quotes) => {
  return quotes.reduce((total, quote) => total + (quote.quoteAmount || 0), 0);
};

/**
 * Filter quotes by status
 * @param {Array} quotes - Array of quote objects
 * @param {string} status - Status to filter by
 * @returns {Array} Filtered quotes
 */
export const filterQuotesByStatus = (quotes, status) => {
  if (status === 'all') return quotes;
  return quotes.filter(quote => quote.status === status);
};

/**
 * Sort quotes by date (newest first)
 * @param {Array} quotes - Array of quote objects
 * @returns {Array} Sorted quotes
 */
export const sortQuotesByDate = (quotes) => {
  return [...quotes].sort((a, b) => {
    const dateA = new Date(a.createdAt);
    const dateB = new Date(b.createdAt);
    return dateB - dateA;
  });
};
